import type { TrackZoneType } from '../zones/TrackZoneTypes'
import type { RecalibratedLap } from '../sessionAnalysis/zoneDisplay/recalibratedLapPoints'
import { MIN_LAPS_FOR_CONSISTENCY } from '../config/consistencyScoring'
import { buildMedianSpeedReference, speedDeltaAt } from './speedDeltaSeries'
import {
  buildSignedAccelDisplay,
  type SignedAccelGateReason,
  type SignedAccelLapInput,
} from './signedAccelDisplay'

/** The three metrics a zone chart can plot. */
export type ZoneChartMetric = 'speed' | 'speedDelta' | 'brake'

/**
 * DEFAULT chart metric per zone type — the delta view everywhere (pass 12,
 * owner-locked): the "vs session median" delta is the consistency story on
 * every zone type, and absolute speed is one tap away on the toggle. Below
 * MIN_LAPS_FOR_CONSISTENCY laps the delta is unavailable and the options
 * degrade to speed-first (see zoneChartMetricOptions).
 */
export const ZONE_CHART_METRIC: Record<TrackZoneType, ZoneChartMetric> = {
  braking: 'speedDelta',
  cornering: 'speedDelta',
  acceleration: 'speedDelta',
  straight: 'speedDelta',
}

/** Single source of the zone-chart default metric for both clients. */
export function zoneChartMetric(zoneType: TrackZoneType): ZoneChartMetric {
  return ZONE_CHART_METRIC[zoneType]
}

/** Toggle chip/button labels, single-sourced for both clients. */
export const ZONE_CHART_METRIC_LABELS: Record<ZoneChartMetric, string> = {
  speedDelta: 'Δ',
  speed: 'Speed',
  brake: 'Brake %',
}

/** Caption while the brake metric is active — the honest framing that lets brake% return as an opt-in view. */
export const BRAKE_METRIC_CAPTION =
  'Brake style — laps can differ here while speeds still agree'

/** Title for the telemetry chart's reading-guide affordance, single-sourced for both clients. */
export const CHART_GUIDE_TITLE = 'Reading this chart'

/** Caption explaining the track-map's single reference-point marker (pass
 * 26: the dashed CHART marker line was removed — B2 — so this now
 * describes the map marker only). */
export const CHART_MARKER_CAPTION =
  "Map marker: the biggest inconsistency's location"

/**
 * Web zone charts don't yet render the GPS-derived tier distinctly (mobile
 * only, this pass). Until the web parity follow-up, downgrade 'gps-derived'
 * provenance back to 'calculated-gps' before the shared gate runs, so these
 * laps are treated exactly as they were before the tier — GPS-only, no
 * accelerometer — and never render unmarked here. Genuinely gps-too-sparse /
 * gps-too-noisy laps are NOT downgraded, so they keep their specific reason.
 * Remove this shim when web parity lands.
 */
function gateInputsForWeb(
  laps: { lapId: string; points: RecalibratedLap['points'] }[]
): SignedAccelLapInput[] {
  return laps.map((l) => ({
    lapId: l.lapId,
    points: l.points.map((p) => ({
      speed: p.speed,
      acceleration: p.acceleration,
      accelerationSource:
        p.accelerationSource === 'gps-derived'
          ? ('calculated-gps' as const)
          : p.accelerationSource,
      accelerationSuspect: p.accelerationSuspect,
    })),
  }))
}

/**
 * The zone chart's metric toggle options, defaults first (clients use
 * options[0] as their initial selection). Braking zones offer all three;
 * other types offer speed + delta. Below MIN_LAPS_FOR_CONSISTENCY included
 * laps the delta metric is unavailable (same degradation path as the
 * consistency band returning null) and 'speed' leads.
 */
export function zoneChartMetricOptions(
  zoneType: TrackZoneType,
  includedLapCount: number
): ZoneChartMetric[] {
  const all: ZoneChartMetric[] =
    zoneType === 'braking'
      ? ['speedDelta', 'speed', 'brake']
      : ['speed', 'speedDelta']
  const defaultMetric = zoneChartMetric(zoneType)
  const ordered = [defaultMetric, ...all.filter((m) => m !== defaultMetric)]
  if (includedLapCount < MIN_LAPS_FOR_CONSISTENCY) {
    return ordered.filter((m) => m !== 'speedDelta')
  }
  return ordered
}

export interface ZoneChartPoint {
  /** Recalibrated distance in meters, rounded to whole meters (chart grid key). */
  distance: number
  /**
   * speed (km/h), speed delta vs the session median (km/h), or signed
   * accel/brake percent (−100..100, + = accel, − = brake) per the selected
   * metric. `null` under the brake metric means a gap (a suspect sample or
   * an unreferenced point) — the point stays in the series so
   * `connectNulls={false}` breaks the line there instead of the series
   * silently shrinking.
   */
  value: number | null
  lat: number
  lng: number
}

export interface ZoneChartSeries {
  lapId: string
  lapNumber: number
  dataPoints: ZoneChartPoint[]
}

export interface BuildZoneChartSeriesOptions {
  zoneType: TrackZoneType
  startDistance: number
  endDistance: number
  /** Override the ZONE_CHART_METRIC default — the toggle's selection. */
  metric?: ZoneChartMetric
}

/**
 * Build one per-lap chart series for a zone. The plotted metric comes from
 * `opts.metric ?? zoneChartMetric(zoneType)`: speed (km/h), speed delta vs
 * the per-sample session median (km/h; points without a gated reference are
 * omitted), or signed accel/brake percent (−100..100, via
 * `buildSignedAccelDisplay` — built from exactly the laps passed in, i.e.
 * the zone's compared laps, not the whole session).
 */
export function buildZoneChartSeries(
  laps: RecalibratedLap[],
  opts: BuildZoneChartSeriesOptions
): ZoneChartSeries[] {
  const metric = opts.metric ?? zoneChartMetric(opts.zoneType)
  // Reference over the laps' FULL traces, deliberately — not just the
  // window. A full-trace reference interpolates across the window boundary,
  // so samples near the zone edges still get a covered reference and keep
  // their delta; a window-only reference would lose coverage right at the
  // edges. This also means the builder needs no second slicing pass.
  const reference =
    metric === 'speedDelta'
      ? buildMedianSpeedReference(laps.map((l) => l.points))
      : null
  const signedAccel =
    metric === 'brake' ? buildSignedAccelDisplay(gateInputsForWeb(laps)) : null
  return laps.map((lap) => {
    const signedLap = signedAccel?.laps.get(lap.lapId)
    if (signedAccel && (!signedLap || signedLap.status === 'gated')) {
      // Failed the accuracy gate (GPS-only or unknown provenance) — the lap
      // stays in the series list (so callers can still count/label it) but
      // draws nothing.
      return { lapId: lap.lapId, lapNumber: lap.lapNumber, dataPoints: [] }
    }
    const dataPoints: ZoneChartPoint[] = []
    // Resolve each signed point by its ORIGINAL index — signedLap.points is
    // index-aligned with lap.points, not with the distance-filtered output —
    // so the index has to be captured before the bounds check below skips it.
    lap.points.forEach((p, i) => {
      if (p.distance < opts.startDistance || p.distance > opts.endDistance) {
        return
      }
      let value: number | null
      if (metric === 'brake') {
        const sp = signedLap!.points[i]
        value = sp && !sp.gap ? sp.signedPercent : null
      } else if (reference) {
        const delta = speedDeltaAt(reference, p)
        if (delta === null) return // < MIN_LAPS coverage here — omit the point
        value = delta
      } else {
        value = p.speed
      }
      dataPoints.push({
        distance: Math.round(p.distance),
        value,
        lat: p.lat,
        lng: p.lng,
      })
    })
    return { lapId: lap.lapId, lapNumber: lap.lapNumber, dataPoints }
  })
}

/**
 * Lap ids the brake metric's accuracy gate excludes from the zone chart
 * (GPS-only or unknown-provenance acceleration — see
 * `buildSignedAccelDisplay`/`lapGateReason`). Feeds the web notice that
 * names the omitted laps instead of letting the series quietly shrink.
 */
export function zoneSeriesGatedLapIds(laps: RecalibratedLap[]): string[] {
  return buildSignedAccelDisplay(gateInputsForWeb(laps)).gatedLapIds
}

/**
 * Gate reasons for the excluded laps (index-unrelated to `zoneSeriesGatedLapIds`
 * — one entry per gated lap, order not significant). Feeds `describeGatedLaps`
 * so the web notice can distinguish genuinely GPS-only laps from
 * quality-unknown ones instead of captioning every gated lap as "GPS-only".
 */
export function zoneSeriesGatedLapReasons(
  laps: RecalibratedLap[]
): SignedAccelGateReason[] {
  const display = buildSignedAccelDisplay(gateInputsForWeb(laps))
  const reasons: SignedAccelGateReason[] = []
  for (const lapId of display.gatedLapIds) {
    const reason = display.laps.get(lapId)?.gateReason
    if (reason) reasons.push(reason)
  }
  return reasons
}

/**
 * Fit a speed y-axis to the data with 10% padding (±5 when flat), floored
 * at 0. Shared so the web zone chart matches mobile's `fitYToData` axis.
 */
export function fitSpeedAxisBounds(
  rawMinY: number,
  rawMaxY: number
): { minY: number; maxY: number } {
  const range = rawMaxY - rawMinY
  const pad = range > 0 ? range * 0.1 : 5
  return { minY: Math.max(0, rawMinY - pad), maxY: rawMaxY + pad }
}
